/**
 * hemēka · panier (chargé une fois pour tout le site, snippets/scripts.liquid).
 * Repris de pop-cbd (pop-cart.js, pop-panier-vide.js, pop-dealeasy.js) et de saveurs-cbd (sv-reassurance.js).
 *
 * 1. signalerAjoutPanier : prévient le thème qu'un ajout a été fait HORS de ses boutons. Émet l'événement
 *    standard d'ajout avec le HTML du tiroir : le tiroir se redessine et s'ouvre comme après le bouton du
 *    thème (réglage « ouvrir le tiroir après ajout »). Sans le HTML, il s'ouvrait sur « panier vide ».
 * 2. Boutons « Ajouter » / « + » du panier vide et des suggestions ([data-hk-ajouter]) : ajout sans quitter la page.
 * 3. Dealeasy redirige vers /cart après son propre bouton d'ajout : son action est remplacée par
 *    « rafraîchir le panier » (écoute en capture sur window, avant celle de Dealeasy posée sur document),
 *    puis l'événement d'ajout est émis. Le réglage de l'application n'est pas modifié.
 * 4. Heure limite d'expédition ([data-hk-expedition]) : « Commandez dans 2 h 13 min : expédié aujourd'hui »,
 *    sinon le prochain jour ouvré (lundi-vendredi, hors fériés français et jours de fermeture saisis).
 * 5. Dealeasy modifie le panier lui-même (cadeau validé dans sa fenêtre, cadeau retiré sous le palier) puis
 *    émet « cart:refresh », qu'Horizon n'écoute pas : le tiroir gardait l'ancien contenu ET ses numéros de
 *    ligne, si bien qu'un clic « supprimer » retirait la mauvaise ligne (testé le 18/10). On redessine le
 *    tiroir à ce signal. Sans boucle : Dealeasy ne réagit qu'aux requêtes /cart/add|change|update|clear,
 *    pas à la lecture du panier faite ici.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const racine = () => window.Shopify?.routes?.root || '/';

/**
 * @param {string} source - qui a ajouté
 * @param {'add'|'update'} [action] - « add » ouvre le tiroir (réglage du thème), « update » le redessine seulement
 * @returns {Promise<void>}
 */
export function signalerAjoutPanier(source, action = 'add') {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent(
      action === 'add'
        ? { action: 'add', context: 'product', lines: [], promise: deferred.promise }
        : { action: 'update', context: 'cart', lines: [], promise: deferred.promise }
    )
  );
  const ids = [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
    .filter(Boolean);
  const sections = ids.length
    ? fetch(`${racine()}?sections=${[...new Set(ids)].join(',')}`).then((r) => r.json()).catch(() => undefined)
    : Promise.resolve(undefined);
  return Promise.all([fetch(`${racine()}cart.js`).then((r) => r.json()), sections])
    .then(([cart, html]) =>
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source, sections: html },
      })
    )
    .catch(deferred.reject);
}

if (!window.hkPanier) {
  window.hkPanier = true;

  // 2. Boutons d'ajout du panier vide et des suggestions
  document.addEventListener('click', (event) => {
    const bouton = /** @type {HTMLButtonElement|null} */ (
      event.target instanceof Element ? event.target.closest('[data-hk-ajouter]') : null
    );
    if (!bouton || bouton.disabled) return;
    event.preventDefault();
    bouton.disabled = true;
    fetch(`${racine()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(bouton.dataset.hkAjouter), quantity: 1 }] }),
    })
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        return signalerAjoutPanier('hk-cart');
      })
      .catch(() => {
        bouton.disabled = false;
      });
  });

  // 3. Dealeasy : pas de redirection vers /cart, le tiroir s'ouvre
  window.addEventListener(
    'lbDealeasyAddProductsToCart',
    (event) => {
      const detail = /** @type {CustomEvent} */ (event).detail;
      if (!detail || typeof detail !== 'object') return;
      detail.settings = { ...(detail.settings || {}), atcBtnAction: 'refresh_cart' };
    },
    true
  );
  window.addEventListener('lb-dealeasy-qty-selector-added-to-cart', (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (detail?.status !== 'success') return;
    signalerAjoutPanier('hk-dealeasy');
  });

  // 5. Dealeasy a changé le panier de son côté : le tiroir se redessine (signal émis deux fois, regroupé)
  let attenteDealeasy = 0;
  document.addEventListener('cart:refresh', () => {
    clearTimeout(attenteDealeasy);
    attenteDealeasy = window.setTimeout(() => signalerAjoutPanier('hk-dealeasy', 'update'), 120);
  });

  // 4. Heure limite d'expédition
  const JOURS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
  const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
  const cle = (/** @type {Date} */ d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const ajout = (/** @type {Date} */ d, /** @type {number} */ n) => {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
  };
  // Dimanche de Pâques (Meeus/Jones/Butcher)
  const paques = (/** @type {number} */ a) => {
    const b = a % 19, c = Math.floor(a / 100), d = a % 100, e = Math.floor(c / 4), f = c % 4;
    const g = Math.floor((c + 8) / 25), h = Math.floor((c - g + 1) / 3);
    const i = (19 * b + c - e - h + 15) % 30, k = Math.floor(d / 4), l = d % 4;
    const m = (32 + 2 * f + 2 * k - i - l) % 7, n = Math.floor((b + 11 * i + 22 * m) / 451);
    const mois = Math.floor((i + m - 7 * n + 114) / 31), jour = ((i + m - 7 * n + 114) % 31) + 1;
    return new Date(a, mois - 1, jour);
  };
  const feries = (/** @type {number} */ a) => {
    const p = paques(a);
    return new Set([
      `${a}-01-01`, `${a}-05-01`, `${a}-05-08`, `${a}-07-14`, `${a}-08-15`, `${a}-11-01`, `${a}-11-11`, `${a}-12-25`,
      cle(ajout(p, 1)), cle(ajout(p, 39)), cle(ajout(p, 50)),
    ]);
  };
  const ouvre = (/** @type {Date} */ d, /** @type {Set<string>} */ fermes) => {
    const j = d.getDay();
    if (j === 0 || j === 6) return false;
    const k = cle(d);
    return !feries(d.getFullYear()).has(k) && !fermes.has(k);
  };
  const libelle = (/** @type {Date} */ cible, /** @type {Date} */ maintenant) => {
    const ecart = Math.round(
      (new Date(cible.getFullYear(), cible.getMonth(), cible.getDate()).getTime() -
        new Date(maintenant.getFullYear(), maintenant.getMonth(), maintenant.getDate()).getTime()) / 86400000
    );
    if (ecart === 1) return 'demain';
    if (ecart < 7) return JOURS[cible.getDay()];
    return `le ${JOURS[cible.getDay()]} ${cible.getDate()} ${MOIS[cible.getMonth()]}`;
  };
  const echapper = (/** @type {string} */ s) =>
    s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] || c);

  const majExpedition = () => {
    document.querySelectorAll('[data-hk-expedition]').forEach((el) => {
      const e = /** @type {HTMLElement} */ (el);
      const texte = e.querySelector('[data-hk-expedition-texte]');
      if (!texte) return;
      const limite = parseInt(e.dataset.heureLimite || '14', 10);
      const fermes = new Set((e.dataset.joursFermes || '').split(',').map((s) => s.trim()).filter(Boolean));
      const maintenant = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Paris' }));
      const reste = limite * 60 - (maintenant.getHours() * 60 + maintenant.getMinutes());
      let html = '';
      if (ouvre(maintenant, fermes) && reste > 0 && e.dataset.texteAujourdhui) {
        const h = Math.floor(reste / 60), m = reste % 60;
        const duree = h > 0 ? `${h} h ${String(m).padStart(2, '0')} min` : `${m} min`;
        html = echapper(e.dataset.texteAujourdhui).replace('[durée]', `<strong>${duree}</strong>`);
      } else if (e.dataset.texteProchain) {
        let prochain = ajout(maintenant, 1);
        for (let n = 0; n < 30 && !ouvre(prochain, fermes); n++) prochain = ajout(prochain, 1);
        html = echapper(e.dataset.texteProchain).replace('[jour]', `<strong>${libelle(prochain, maintenant)}</strong>`);
      }
      if (html && texte.innerHTML !== html) texte.innerHTML = html;
    });
  };
  let attente = 0;
  const planifier = () => {
    if (attente) return;
    attente = requestAnimationFrame(() => {
      attente = 0;
      majExpedition();
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', majExpedition);
  else majExpedition();
  setInterval(majExpedition, 30000);
  // Le tiroir et la page panier se redessinent après chaque changement : on recalcule à l'arrivée du bloc.
  const concerne = (/** @type {Node} */ n) => {
    const el = n instanceof Element ? n : n.parentElement;
    return Boolean(el && (el.closest('[data-hk-expedition]') || el.querySelector('[data-hk-expedition]')));
  };
  new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === 'characterData' ? concerne(m.target) : [...m.addedNodes].some(concerne)) {
        planifier();
        return;
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true, characterData: true });
}
